import React, { useState } from 'react';

import ToggleInput from 'CommonComponents/ToggleInput';

import { useToast } from 'Hooks/useToast';

/**
 * The project's standard modules, one toggle each. Each toggle saves on its
 * own, reverts if the save fails and shows why. A card that cannot move
 * arrives with its reason, shown as the toggle's tooltip; the save refuses
 * the same change with the same sentence.
 *
 * `cards` seeds the panel once; after that each save's response is the source
 * of truth.
 */
export default function ModuleTogglesPanel({ cards: initialCards, canToggle, updateUrl, onSaved }) {
    const [cards, setCards] = useState(initialCards || []);
    const [savingKey, setSavingKey] = useState(null);

    const toast = useToast();

    const setEnabled = (key, enabled) => setCards((previous) => previous.map((card) => (card.key === key ? { ...card, enabled } : card)));

    const toggle = (card) => {
        const next = !card.enabled;

        setEnabled(card.key, next);
        setSavingKey(card.key);

        axios
            .put(updateUrl, { modules: { [card.key]: next } })
            .then(({ data }) => {
                setCards(data.module_cards || []);
                toast.success(next ? $t('{{module}} is now on', { module: card.title }) : $t('{{module}} is now off', { module: card.title }));
                onSaved?.(data);
            })
            .catch((e) => {
                setEnabled(card.key, card.enabled);

                const errors = e.response?.data?.errors;

                toast.error(
                    e.response?.status === 403
                        ? $t('You do not have permission to change modules')
                        : errors
                          ? Object.values(errors).flat().join(' ')
                          : e.response?.data?.message || $t('Something went wrong'),
                );
            })
            .finally(() => setSavingKey(null));
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-3 tw-rounded-lg tw-border-2 tw-border-gray-200 tw-p-2.5">
            <div className="tw-flex tw-flex-col tw-gap-y-1">
                <div className="tw-font-semibold">{$t('Modules')}</div>
                {!canToggle && <p className="tw-text-2xs tw-text-charcoal-50">{$t('You do not have permission to change the modules on this project.')}</p>}
            </div>

            <div className="tw-flex tw-flex-col">
                {cards.map((card) => (
                    <ToggleInput
                        key={card.key}
                        label={card.title}
                        checked={!!card.enabled}
                        disabled={!canToggle || card.locked || !!card.disabled_reason || savingKey !== null}
                        tooltip={card.locked ? $t('Budgeting is always on.') : card.disabled_reason || undefined}
                        onChange={() => toggle(card)}
                    />
                ))}
            </div>
        </div>
    );
}
